import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize, Subscription } from 'rxjs';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { ContentService } from '../content.service';
import { AiApiService } from '@app/_services/ai/ai-api.service';
import { AiAskRequest, AiConversationTurn } from '@app/_services/ai/ai-api.models';

interface ChatMessage {
    role: 'user' | 'assistant';
    text: string;
    timestamp: Date;
    isContent?: boolean;
}

@Component({
    selector: 'app-content-generate',
    templateUrl: './content-generate.component.html',
    styleUrls: ['./content-generate.component.scss'],
    providers: [MessageService],
    standalone: false
})
export class ContentGenerateComponent implements OnInit, OnDestroy {
    @ViewChild('chatContainer') chatContainer!: ElementRef;

    form!: FormGroup;
    setupForm!: FormGroup;
    loading = false;
    messages: ChatMessage[] = [];
    phase: 'setup' | 'chat' = 'setup';
    lastContentHtml: string = '';

    private sub?: Subscription;
    private bearerToken?: string;

    contentTypes: any[] = [];

    suggestedTopics = [
        'Best things to do in Ka\'anapali',
        'Top snorkeling spots near our resorts',
        'Family-friendly activities in Maui',
        'Romantic dinner spots in West Maui',
        'Guide to Haleakala sunrise experience',
        'Park City ski season essentials',
    ];

    constructor(
        private fb: FormBuilder,
        private api: AiApiService,
        private contentService: ContentService,
        private messageService: MessageService,
        public ref: DynamicDialogRef,
        public config: DynamicDialogConfig
    ) {
        this.bearerToken = this.config?.data?.bearerToken;
    }

    ngOnInit(): void {
        this.setupForm = this.fb.group({
            contentTypeId: [3, Validators.required],
            topic: ['', [Validators.required, Validators.minLength(5)]],
            locationId: [null],
            developmentId: [null],
            shortTermRentalId: [null],
            adrRegion: [null],
            generatedBy: ['Claude'],
            additionalNotes: [''],
        });
        this.form = this.fb.group({
            question: ['', [Validators.required, Validators.minLength(3)]]
        });
        // load content types from API (Integer-keyed lookup)
        this.contentService.findAllContentTypes().subscribe({
            next: (res: any) => { this.contentTypes = res?.data?.contentTypes || []; },
            error: () => { this.contentTypes = []; }
        });
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }

    startChat(): void {
        this.setupForm.markAllAsTouched();
        if (this.setupForm.invalid) return;

        this.phase = 'chat';
        const v = this.setupForm.value;
        const typeLabel = this.contentTypes.find((t: any) => t.contentTypeId === v.contentTypeId)?.name || 'content';

        let systemPrompt = `Write a ${typeLabel} about: ${v.topic}`;
        if (v.additionalNotes) systemPrompt += `\n\nAdditional notes: ${v.additionalNotes}`;
        if (v.locationId) systemPrompt += `\nLocation ID: ${v.locationId}`;

        this.sendMessage(systemPrompt);
    }

    useSuggestion(topic: string): void {
        this.setupForm.patchValue({ topic: topic });
    }

    sendMessage(questionOverride?: string): void {
        const question = (questionOverride || String(this.form.value.question || '')).trim();
        if (!question || question.length < 3) return;

        this.messages.push({ role: 'user', text: question, timestamp: new Date() });
        this.form.patchValue({ question: '' });
        this.loading = true;
        this.scrollToBottom();

        const history: AiConversationTurn[] = this.messages
            .slice(-10)
            .map(m => ({ role: m.role, text: m.text }));

        const v = this.setupForm.value;
        const typeName = this.contentTypes.find((t: any) => t.contentTypeId === v.contentTypeId)?.name || 'content';
        const contentContext = `[CONTENT GENERATION MODE - Type: ${typeName}]\n`
            + `You are a luxury travel marketing specialist for KBM Resorts. `
            + `Write in an aspirational, evocative tone. Avoid cliches like "stunning", "breathtaking", "world-class". `
            + `Use sensory details. No exclamation marks. `
            + `When you generate the final content, format it with HTML tags (h2, h3, p, ul, li). `
            + `Include a natural mention of KBM Resorts near the end.\n\n`;

        const payload: AiAskRequest = {
            question: contentContext + question,
            preferredDomain: 'content',
            conversationHistory: history
        };

        this.sub = this.api.ask(payload, this.bearerToken)
            .pipe(finalize(() => {
                this.loading = false;
                this.scrollToBottom();
            }))
            .subscribe({
                next: (res: any) => {
                    const text = res?.data?.answer?.text
                        || res?.data?.answer
                        || res?.data?.content?.bodyHtml
                        || JSON.stringify(res?.data);
                    const msg: ChatMessage = {
                        role: 'assistant',
                        text: typeof text === 'string' ? text : JSON.stringify(text),
                        timestamp: new Date(),
                        isContent: true
                    };
                    this.messages.push(msg);
                    this.lastContentHtml = msg.text;
                },
                error: (err: any) => {
                    this.messages.push({
                        role: 'assistant',
                        text: 'Error: ' + (err?.error?.message || err?.message || 'Generation failed'),
                        timestamp: new Date()
                    });
                }
            });
    }

    ask(): void {
        this.sendMessage();
    }

    saveAsDraft(): void {
        if (!this.lastContentHtml) {
            this.messageService.add({ severity: 'warn', summary: 'No content', detail: 'Generate content first before saving.' });
            return;
        }

        const v = this.setupForm.value;
        const slug = v.topic.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

        const content: any = {
            contentTypeId: v.contentTypeId,
            title: v.topic,
            slug: slug,
            bodyHtml: this.lastContentHtml,
            status: 'DRAFT',
            generatedBy: v.generatedBy || 'Claude',
            enabled: true,
            locationId: v.locationId || null,
            developmentId: v.developmentId || null,
            shortTermRentalId: v.shortTermRentalId || null,
            adrRegion: v.adrRegion || null,
        };

        this.loading = true;
        this.contentService.add(content).subscribe({
            next: (res: any) => {
                this.loading = false;
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Content saved as DRAFT. Edit in CMS to refine and publish.', life: 5000 });
                this.ref.close(res?.data?.content);
            },
            error: (err: any) => {
                this.loading = false;
                this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: err?.error?.message || 'Error saving content' });
            }
        });
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.ask();
        }
    }

    clearChat(): void {
        this.messages = [];
        this.lastContentHtml = '';
        this.phase = 'setup';
    }

    private scrollToBottom(): void {
        setTimeout(() => {
            if (this.chatContainer?.nativeElement) {
                this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
            }
        }, 100);
    }
}
